export type Pos = [number, number];
export type PixelPoint = { x: number; y: number };
export type BBox = [number, number, number, number];

export type Continent =
  | "Africa" | "Asia" | "Europe" | "NorthAmerica" | "SouthAmerica" | "Oceania";
export type RegionScope = Continent | "World";
export type Difficulty = "Easy" | "Medium" | "Hard" | "VeryHard";
export type GameStatus =
  | "Idle" | "Loading" | "Playing" | "Paused" | "Completed" | "Abandoned" | "LoadError";

export interface MultiPolygon {
  type: "MultiPolygon";
  coordinates: number[][][][];
}

export interface LandPart {
  partId: string;
  geometry: MultiPolygon;
  centroid: Pos;
  bboxDiagonalKm: number;
  areaKm2: number;
}

export interface CountryFeature {
  id: string;
  wikidataId: string;
  name: string;
  nameEn: string;
  continent: Continent;
  areaKm2: number;
  parts: LandPart[];
}

export interface RegionData {
  schemaVersion: number;
  regionScope: RegionScope;
  difficulty: Difficulty;
  countries: CountryFeature[];
}

export interface PuzzlePiece {
  pieceId: string;
  countryId: string;
  countryName: string;
  countryNameEn: string;
  geometry: MultiPolygon;
  target: Pos;
  bbox: BBox;
  isLocked: boolean;
}

export interface GameSession {
  sessionId: string;
  regionScope: RegionScope;
  difficulty: Difficulty;
  status: GameStatus;
  pieces: PuzzlePiece[];
  trayOrder: string[];
  mistakeCount: number;
  hintUsedCount: number;
  startedAt: number;
  pausedAt: number | null;
  accumulatedPauseMs: number;
  elapsedSeconds: number;
}

export interface PlacementResult {
  pieceId: string;
  correct: boolean;
  distancePx: number;
  allowedRadiusPx: number;
  lockedCount: number;
  totalCount: number;
  completed: boolean;
}

export interface ScoreResult {
  baseScore: number;
  penalty: number;
  finalScore: number;
  percentage: number;
  stars: 0 | 1 | 2 | 3;
  elapsedSeconds: number;
  mistakeCount: number;
  hintUsedCount: number;
}

export interface BestRecord {
  key: string;
  bestScore: number;
  bestTimeSeconds: number;
  stars: 0 | 1 | 2 | 3;
  achievedAt: string;
}

export interface HintResult {
  pieceId: string;
  target: Pos;
  bbox: BBox;
  geometry: MultiPolygon;
  hintsRemaining: number;
}

/** 把經緯度換算成畫面像素座標；由渲染層（Leaflet）注入，核心邏輯不直接相依地圖函式庫 */
export interface Projection {
  toPixel(pos: Pos): PixelPoint;
}

export interface DifficultyConfig {
  toleranceRatio: number;
  timePenaltyPerSecond: number;
  hintLimit: number;
}

// toleranceRatio 是「拼圖片螢幕外接框對角線」的比例。初版訂 0.04~0.12 實測後過緊：
// 挪威畫面上 167px 寬卻只能命中中央 20px，看起來明明蓋住了卻判定失敗。
export const DIFFICULTY_CONFIG: Record<Difficulty, DifficultyConfig> = {
  Easy: { toleranceRatio: 0.45, timePenaltyPerSecond: 1.0, hintLimit: Infinity },
  Medium: { toleranceRatio: 0.32, timePenaltyPerSecond: 1.5, hintLimit: 5 },
  Hard: { toleranceRatio: 0.22, timePenaltyPerSecond: 2.0, hintLimit: 3 },
  VeryHard: { toleranceRatio: 0.14, timePenaltyPerSecond: 2.5, hintLimit: 0 },
};

// 摩納哥這類小國照比例換算不到 200 公尺，在世界地圖縮放層級下是次像素、永遠點不中（SRS §7.4）
export const MIN_TOLERANCE_PX = 20;

export const BASE_SCORE_PER_PIECE = 100;
export const MISTAKE_PENALTY = 20;
export const HINT_PENALTY = 50;

export class InvalidPieceStateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidPieceStateError";
  }
}

export class HintExhaustedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "HintExhaustedError";
  }
}

export class StorageWriteError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "StorageWriteError";
  }
}

export class StorageReadError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "StorageReadError";
  }
}
